import { makeAutoObservable } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";
import { Contact } from "types/common/ServiceStoreTypes";

export class ContactsListStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  filtersVisible = true;

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  toggleFilters() {
    this.filtersVisible = !this.filtersVisible;
  }

  // Async actions
  async loadContacts() {
    this.loading = true;
    this.error = null;

    try {
      // Load all relational data to ensure complete information is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.contactsStore.find({ query: { $sort: { lastName: 1, firstName: 1 } } }),
        this.accountsStore.find(),
        this.opportunitiesStore.find(),
        this.activitiesStore.find(),
      ]);
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load contacts";
      console.error("Failed to load contacts:", error);
    } finally {
      this.loading = false;
    }
  }

  async reloadContacts() {
    await this.loadContacts();
  }

  async createContact(contactData: Partial<Contact>) {
    try {
      const contact = (await this.contactsStore.create({
        ...contactData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as unknown as Contact;

      // The service store will automatically update its entities
      return contact;
    } catch (error) {
      console.error("Failed to create contact:", error);
      throw error;
    }
  }

  async updateContact(contactId: number, updates: Partial<Contact>) {
    try {
      const updated = (await this.contactsStore.patch(contactId, {
        ...updates,
        updatedAt: new Date().toISOString(),
      })) as unknown as Contact;

      // The service store will automatically update its entities
      return updated;
    } catch (error) {
      console.error("Failed to update contact:", error);
      throw error;
    }
  }

  async deleteContact(contactId: number) {
    try {
      await this.contactsStore.remove(contactId);

      // The service store will automatically update its entities
    } catch (error) {
      console.error("Failed to delete contact:", error);
      throw error;
    }
  }

  async createOpportunityForContact(
    contactId: number,
    opportunityData: Record<string, unknown>
  ) {
    try {
      const contact = this.contacts.find((c) => (c as any).id === contactId);
      if (!contact) throw new Error("Contact not found");

      return await this.opportunitiesStore.create({
        ...opportunityData,
        contactId: contactId,
        accountId: contact.accountId,
      });
    } catch (error) {
      console.error("Failed to create opportunity for contact:", error);
      throw error;
    }
  }

  async addActivityToContact(
    contactId: number,
    activityData: Record<string, unknown>
  ) {
    try {
      const contact = this.contacts.find((c) => (c as any).id === contactId);
      if (!contact) throw new Error("Contact not found");

      return await this.activitiesStore.create({
        ...activityData,
        contactId: contactId,
        accountId: contact.accountId,
      });
    } catch (error) {
      console.error("Failed to add activity to contact:", error);
      throw error;
    }
  }

  // Computed values
  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  get contacts(): Contact[] {
    return this.contactsStore.entities as unknown[] as Contact[];
  }

  get hasFilters(): boolean {
    return false; // Filters are now handled by the datagrid
  }

  get primaryContacts(): Contact[] {
    return this.contacts.filter((contact) => contact.isPrimaryContact);
  }

  get contactsByDepartment(): Record<string, Contact[]> {
    return this.contacts.reduce((acc, contact) => {
      const dept = contact.department || "Unknown";
      if (!acc[dept]) {
        acc[dept] = [];
      }
      acc[dept].push(contact);
      return acc;
    }, {} as Record<string, Contact[]>);
  }

  get contactsByAccount(): Record<string, Contact[]> {
    return this.contacts.reduce((acc, contact) => {
      const accountId = contact.accountId?.toString() || "No Account";
      if (!acc[accountId]) {
        acc[accountId] = [];
      }
      acc[accountId].push(contact);
      return acc;
    }, {} as Record<string, Contact[]>);
  }
}

export default ContactsListStore;
